import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/drone/src/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useMemo } from 'react';
import { useControlledState } from '@deliveryhero/armor-system';
import { useCommonDatePickerPanel } from '../../hooks/useCommonDatePickerPanel';
import { useCommonDatePickerState } from '../../hooks/useCommonDatePickerState';
import { useCommonDatePickerCallbacks } from '../../hooks/useCommonDatePickerCallbacks';
import { DateVectorRange } from '../../utils/DateVectorRange';
import { useDatePickerSelectionEvents } from './useDatePickerSelectionEvents';
import { useCommonDatePickerAllowedDates } from '../../hooks/useCommonDatePickerAllowedDates';
import { externalizeValue } from '../utils/externalizeValue';
import { useDatePickerFormattedValue } from './useDatePickerFormattedValue';
import { useCommonDatePickerInputProps } from '../../hooks/useCommonDatePickerInputProps';
import { useDatePickerTodayButton } from './useDatePickerTodayButton';
import { useCommonDatePickerActionButtons } from '../../hooks/useCommonDatePickerActionButtons';
import { useCommonDatePickerCompatibility } from '../../hooks/useCommonDatePickerCompatibility';
import { useDatePickerMaskedInput } from './useDatePickerMaskedInput';
export var useDatePicker = function useDatePicker(props, ref) {
  var enableTimePicker = props.enableTimePicker,
    enableMinWidthAutoCorrection = props.enableMinWidthAutoCorrection,
    defaultDateValue = props.defaultDateValue,
    dateValue = props.dateValue,
    onDateValueChange = props.onDateValueChange,
    yearRange = props.yearRange,
    enableMonthYearPickerMode = props.enableMonthYearPickerMode,
    isMonthAllowed = props.isMonthAllowed,
    timePicker24HourFormat = props.timePicker24HourFormat;
  // controlled and uncontrolled state: dateValue, defaultDateValue and onDateValueChange mapped to internalValue
  var _useControlledState = useControlledState(defaultDateValue, dateValue, onDateValueChange),
    _useControlledState2 = _slicedToArray(_useControlledState, 2),
    externalValue = _useControlledState2[0],
    setExternalValue = _useControlledState2[1];
  // internalValue as an object
  var internalValue = useMemo(function () {
    return DateVectorRange.createFromLocalDate(externalValue);
  }, [externalValue]);
  var _useCommonDatePickerC = useCommonDatePickerCompatibility(props),
    enableApplyButton = _useCommonDatePickerC.enableApplyButton;
  var _useCommonDatePickerP = useCommonDatePickerPanel({
      ref: ref
    }, props),
    rootRef = _useCommonDatePickerP.rootRef,
    inputRef = _useCommonDatePickerP.inputRef,
    dropdownOpen = _useCommonDatePickerP.dropdownOpen,
    toggleDropdown = _useCommonDatePickerP.toggleDropdown,
    closeDropdown = _useCommonDatePickerP.closeDropdown,
    arrowProps = _useCommonDatePickerP.arrowProps,
    dropdownProps = _useCommonDatePickerP.dropdownProps,
    portalProps = _useCommonDatePickerP.portalProps;
  var _useCommonDatePickerS = useCommonDatePickerState({
      dropdownOpen: dropdownOpen,
      internalValue: internalValue
    }, props),
    dirtyInternalValueVector = _useCommonDatePickerS.dirtyInternalValueVector,
    setDirtyInternalValueVector = _useCommonDatePickerS.setDirtyInternalValueVector,
    timeSelectorValue = _useCommonDatePickerS.timeSelectorValue,
    setTimeSelectorValue = _useCommonDatePickerS.setTimeSelectorValue,
    displayedDateVector = _useCommonDatePickerS.displayedDateVector,
    setDisplayedDateVector = _useCommonDatePickerS.setDisplayedDateVector,
    currentDateVector = _useCommonDatePickerS.currentDateVector,
    monthYearSelectorOpen = _useCommonDatePickerS.monthYearSelectorOpen,
    onMonthYearSelectorToggle = _useCommonDatePickerS.onMonthYearSelectorToggle;
  var _useCommonDatePickerA = useCommonDatePickerAllowedDates({
      dropdownOpen: dropdownOpen,
      currentDateVector: currentDateVector
    }, props),
    isDateAllowed = _useCommonDatePickerA.isDateAllowed,
    isDateFree = _useCommonDatePickerA.isDateFree,
    isDateSelectable = _useCommonDatePickerA.isDateSelectable;
  var _useCommonDatePickerC2 = useCommonDatePickerCallbacks({
      setDirtyInternalValueVector: setDirtyInternalValueVector,
      setExternalValue: setExternalValue,
      timeSelectorValue: timeSelectorValue,
      setTimeSelectorValue: setTimeSelectorValue,
      dirtyInternalValueVector: dirtyInternalValueVector,
      externalizeValue: externalizeValue,
      enableApplyButton: enableApplyButton
    }, props),
    onDateTimeChange = _useCommonDatePickerC2.onDateTimeChange,
    onTimeSelectorValueChange = _useCommonDatePickerC2.onTimeSelectorValueChange,
    applyValue = _useCommonDatePickerC2.applyValue,
    clearValue = _useCommonDatePickerC2.clearValue;
  var _useDatePickerTodayBu = useDatePickerTodayButton({
      currentDateVector: currentDateVector,
      onDateTimeChange: onDateTimeChange,
      setDisplayedDateVector: setDisplayedDateVector
    }, props),
    onTodayButtonClick = _useDatePickerTodayBu.onTodayButtonClick;
  var _useCommonDatePickerA2 = useCommonDatePickerActionButtons({
      applyValue: applyValue,
      clearValue: clearValue,
      closeDropdown: closeDropdown,
      enableApplyButton: enableApplyButton
    }, props),
    showActions = _useCommonDatePickerA2.showActions,
    actionBarProps = _useCommonDatePickerA2.actionBarProps,
    enableCloseOnSelect = _useCommonDatePickerA2.enableCloseOnSelect;
  var selectionEventProps = useDatePickerSelectionEvents({
    value: dirtyInternalValueVector,
    onChange: onDateTimeChange,
    isDateSelectable: isDateSelectable,
    enableCloseOnSelect: enableCloseOnSelect,
    closeDropdown: closeDropdown,
    enableTimePicker: enableTimePicker,
    timeSelectorValue: timeSelectorValue
  }, props);
  var formattedValue = useDatePickerFormattedValue({
    internalValue: internalValue
  }, props);
  var _useCommonDatePickerI = useCommonDatePickerInputProps({}, props),
    inputProperties = _useCommonDatePickerI.inputProperties,
    inactive = _useCommonDatePickerI.inactive;
  var _useDatePickerMaskedI = useDatePickerMaskedInput({
      formattedValue: formattedValue,
      inactive: inactive,
      toggleDropdown: toggleDropdown,
      setDisplayedDateVector: setDisplayedDateVector,
      onDateTimeChange: onDateTimeChange,
      enableTimePicker: enableTimePicker,
      isDateSelectable: isDateSelectable,
      onTimeSelectorValueChange: onTimeSelectorValueChange,
      dropdownOpen: dropdownOpen,
      enableMonthYearPickerMode: enableMonthYearPickerMode
    }, props),
    value = _useDatePickerMaskedI.value,
    showMaskedInput = _useDatePickerMaskedI.showMaskedInput,
    onChange = _useDatePickerMaskedI.onChange,
    onFocus = _useDatePickerMaskedI.onFocus;
  var onRootClick = inactive ? undefined : toggleDropdown;
  return {
    rootProps: _objectSpread(_objectSpread({}, props), {}, {
      enableTimePicker: enableTimePicker,
      enableMinWidthAutoCorrection: enableMinWidthAutoCorrection,
      ref: rootRef
    }),
    inputProps: _objectSpread(_objectSpread({}, inputProperties), {}, {
      onRootClick: onRootClick,
      ref: inputRef,
      value: formattedValue
    }),
    inputMaskProps: _objectSpread(_objectSpread({}, inputProperties), {}, {
      value: value,
      onChange: onChange,
      onFocus: onFocus
    }),
    onInputIconClick: onRootClick,
    showMaskedInput: showMaskedInput,
    portalProps: portalProps,
    dropdownProps: dropdownProps,
    arrowProps: arrowProps,
    topBarProps: {
      displayedDateVector: displayedDateVector,
      onDisplayedDateVectorChange: setDisplayedDateVector,
      monthYearSelectorOpen: monthYearSelectorOpen,
      onMonthYearToggleClick: onMonthYearSelectorToggle
    },
    daySelectorProps: _objectSpread({
      displayedDateVector: displayedDateVector,
      dirtyInternalValueVector: dirtyInternalValueVector,
      currentDateVector: currentDateVector,
      isDateAllowed: isDateAllowed,
      isDateFree: isDateFree
    }, selectionEventProps),
    monthYearSelectorProps: {
      displayedDateVector: displayedDateVector,
      onDisplayedDateVectorChange: setDisplayedDateVector,
      toggleMonthYearSelector: onMonthYearSelectorToggle,
      yearRange: yearRange,
      enableMonthYearPickerMode: enableMonthYearPickerMode,
      onDateTimeChange: onDateTimeChange,
      isMonthAllowed: isMonthAllowed
    },
    timeSelectorProps: {
      value: timeSelectorValue,
      onChange: onTimeSelectorValueChange,
      enable24HourFormat: timePicker24HourFormat
    },
    displayMonthYearSelector: monthYearSelectorOpen,
    open: dropdownOpen,
    showTimePicker: !!enableTimePicker && !enableMonthYearPickerMode,
    showActions: showActions,
    actionBarProps: _objectSpread(_objectSpread({}, actionBarProps), {}, {
      onTodayButtonClick: onTodayButtonClick
    })
  };
};